import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getTargets, createTarget, createVisit } from '../services/api';

const TargetList = () => {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [visitModal, setVisitModal] = useState({ show: false, target: null });
  const [visitDate, setVisitDate] = useState('');

  useEffect(() => {
    loadTargets();
  }, []);

  const loadTargets = async () => {
    try {
      const response = await getTargets();
      setTargets(response.data);
    } catch (err) {
      setError('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createTarget(formData);
      setFormData({ name: '', notes: '' });
      setShowForm(false);
      loadTargets();
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nella creazione');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Mai visitato';
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  const openVisitModal = (target) => {
    setVisitModal({ show: true, target });
    setVisitDate('');
  };

  const closeVisitModal = () => {
    setVisitModal({ show: false, target: null });
    setVisitDate('');
  };

  const handleAddVisit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createVisit({
        targetId: visitModal.target._id,
        date: visitDate
      });
      closeVisitModal();
      loadTargets();
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nell\'aggiunta della visita');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (target) => {
    if (target.hasConfirmedProposal) {
      return <span className="badge badge-confirmed">Visita programmata</span>;
    }
    if (target.hasOpenProposal) {
      return <span className="badge badge-open">Proposta aperta</span>;
    }
    return <span className="badge badge-none">Nessuna proposta</span>;
  };

  if (loading) return <div className="loading">Caricamento...</div>;

  return (
    <div className="page-container">
      <div className="page-header-flex">
        <h1>Persone da visitare</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn btn-primary">
          {showForm ? 'Annulla' : 'Aggiungi persona'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-group">
            <label htmlFor="name">Nome</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              autoFocus
            />
          </div>
          <div className="form-group">
            <label htmlFor="notes">Note (opzionale)</label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="3"
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Salvataggio...' : 'Aggiungi'}
          </button>
        </form>
      )}

      {targets.length === 0 ? (
        <p className="empty-state">Nessuna persona nella lista</p>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Ultima visita</th>
                <th>Stato</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {targets.map((target) => (
                <tr key={target._id}>
                  <td className="target-name" data-label="Nome">{target.name}</td>
                  <td className="visit-date" data-label="Ultima visita">{formatDate(target.lastVisit)}</td>
                  <td data-label="Stato">{getStatusBadge(target)}</td>
                  <td className="actions-cell">
                    <Link to={`/targets/${target._id}`} className="btn btn-small btn-primary">
                      Dettagli
                    </Link>
                    <button
                      onClick={() => openVisitModal(target)}
                      className="btn btn-small btn-secondary"
                    >
                      Aggiungi visita
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {visitModal.show && (
        <div className="modal-overlay" onClick={closeVisitModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Aggiungi visita passata</h2>
              <button className="modal-close" onClick={closeVisitModal}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Aggiungi una visita per <strong>{visitModal.target?.name}</strong></p>
              <form onSubmit={handleAddVisit}>
                <div className="form-group">
                  <label htmlFor="visitDate">Data della visita</label>
                  <input
                    type="date"
                    id="visitDate"
                    value={visitDate}
                    onChange={(e) => setVisitDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={closeVisitModal}>
                    Annulla
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Salvataggio...' : 'Aggiungi'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TargetList;
